import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';
import { loginAsUser, hasAdminAccess } from '../utils/auth';
import { getActionErrorMessage } from '../utils/errorHandler';
import SearchableDropdown from './SearchableDropdown';
import "../styles/theme.css";
import "../styles/ManageUsers.css";
import "../styles/SearchableDropdown.css";
import ModifyUserModal from './ModifyUserModal';
import ViewUserDocumentsModal from './ViewUserDocumentsModal';
import ExportData from './ExportData';
import { prepareExportData } from '../utils/exportUtils';
import { normalizeUsers } from '../utils/userUtils';
import '../styles/ExportComponents.css';
import { FiUsers, FiFilter, FiSearch, FiUserCheck, FiUserX, FiUserPlus } from 'react-icons/fi';
import LoadingSpinner from './LoadingSpinner';
import '../styles/AdminToolsShell.css';
import { formatPreceptorRanks, getPreceptorRankIds, findRank } from '../utils/rankUtils';

const ManageUsers = () => {
    const [selectedTypes, setSelectedTypes] = useState([]);
    const [students, setStudents] = useState([]);
    const [preceptors, setPreceptors] = useState([]);
    const [preceptorAdmins, setPreceptorAdmins] = useState([]);
    const [standaloneAdmins, setStandaloneAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [specialities, setSpecialities] = useState([]);
    const [institutions, setInstitutions] = useState([]);
    const [ranks, setRanks] = useState([]);
    const [activeFilterPopup, setActiveFilterPopup] = useState(null);
    const [allData, setAllData] = useState({
        students: [],
        preceptors: [],
        preceptorAdmins: [],
        standaloneAdmins: []
    });
    const [studentRank, setStudentRank] = useState('');
    const [preceptorRank, setPreceptorRank] = useState('');
    const [preceptorSpeciality, setPreceptorSpeciality] = useState('');
    const [preceptorInstitution, setPreceptorInstitution] = useState('');
    const [adminType, setAdminType] = useState('all');
    const [selectedSpecialities, setSelectedSpecialities] = useState([]);
    const [selectedInstitutions, setSelectedInstitutions] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [showModifyModal, setShowModifyModal] = useState(false);
    const [modifyUserData, setModifyUserData] = useState({ userId: null, userType: null });
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [showDocsModal, setShowDocsModal] = useState(false);
    const [docsModalData, setDocsModalData] = useState({ userId: null, userName: null });
    const navigate = useNavigate();

    const userTypes = [
        { value: 'student', label: 'Students', icon: <FiUsers className="type-icon" /> },
        { value: 'preceptor', label: 'Preceptors', icon: <FiUserCheck className="type-icon" /> },
        { value: 'admin', label: 'Administrators', icon: <FiUserPlus className="type-icon" /> }
    ];

    const refreshData = async () => {
        setLoading(true);
        setError('');
        try {
            const [studentsRes, preceptorsRes, adminsRes] = await Promise.all([
                axiosInstance.get('/crud/get_all_students'),
                axiosInstance.get('/crud/get_all_preceptors'),
                axiosInstance.get('/crud/get_all_admins')
            ]);

            if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('Fetched data:', {
                students: studentsRes.data,
                preceptors: preceptorsRes.data,
                admins: adminsRes.data
            });

            setAllData({
                students: normalizeUsers(studentsRes.data || []),
                preceptors: normalizeUsers(preceptorsRes.data || []),
                preceptorAdmins: normalizeUsers(adminsRes.data.preceptor_admins || []),
                standaloneAdmins: normalizeUsers(adminsRes.data.standalone_admins || [])
            });
        } catch (err) {
            setError(getActionErrorMessage(err, "fetch users"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refreshData();
    }, []); // Only fetch once when component mounts

    useEffect(() => {
        setStudents(selectedTypes.includes('student') ? allData.students : []);
        setPreceptors(selectedTypes.includes('preceptor') ? allData.preceptors : []);
        setPreceptorAdmins(selectedTypes.includes('admin') ? allData.preceptorAdmins : []);
        setStandaloneAdmins(selectedTypes.includes('admin') ? allData.standaloneAdmins : []);
    }, [selectedTypes, allData]);

    useEffect(() => {
        const fetchFilterOptions = async () => {
            try {
                const [specialitiesRes, institutionsRes, ranksRes] = await Promise.all([
                    axiosInstance.get('/crud/get_all_specialities'),
                    axiosInstance.get('/crud/get_all_institutions'),
                    axiosInstance.get('/ranks/ranks')
                ]);
                setSpecialities(specialitiesRes.data);
                setInstitutions(institutionsRes.data);
                setRanks(Array.isArray(ranksRes.data) ? ranksRes.data : []);
            } catch (err) {
                console.error('Failed to fetch filter options:', err);
            }
        };

        fetchFilterOptions();
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (activeFilterPopup && !event.target.closest('.filter-popup')) {
                setActiveFilterPopup(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [activeFilterPopup]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.action-dropdown')) {
                const dropdowns = document.querySelectorAll('.action-dropdown');
                dropdowns.forEach(dropdown => {
                    const button = dropdown.querySelector('.dropdown-menu');
                    if (button) {
                        button.style.display = 'none';
                    }
                });
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Body scroll lock when delete modal is open (same as ViewAdminSchedules / ModifyUserModal)
    useEffect(() => {
        if (!showDeleteModal) return;
        const prevOverflow = document.body.style.overflow;
        const prevPaddingRight = document.body.style.paddingRight;
        document.body.style.overflow = 'hidden';
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;
        return () => {
            document.body.style.overflow = prevOverflow;
            document.body.style.paddingRight = prevPaddingRight;
        };
    }, [showDeleteModal]);

    const mapUserTypeToTargetRole = (userType) => {
        if (!userType) return '';
        if (userType.includes('student')) return 'student';
        if (userType.includes('preceptor')) return 'preceptor';
        if (userType.includes('admin')) return 'admin';
        return '';
    };

    const handleLoginAs = async (userId, userType) => {
        try {
            if (!userId) {
                console.error('🔴 [IMPERSONATION][ManageUsers] Missing userId in handleLoginAs');
                setError('Unable to login as selected user: missing user id.');
                return;
            }

            const returnPath = window.location.pathname || '/AdminDashboard/manage-users';
            const targetRole = mapUserTypeToTargetRole(userType);
            if (import.meta.env.VITE_DEBUG_MODE === 'true') {
                console.group('🟦 [IMPERSONATION][ManageUsers] Login as User clicked');
                console.log('Target userId:', userId);
                console.log('Target role hint:', targetRole || '(none)');
                console.log('Return path:', returnPath);
                console.log('Pre-call storage snapshot:', {
                    role: localStorage.getItem('role'),
                    isAdmin: localStorage.getItem('isAdmin'),
                    user_id: localStorage.getItem('user_id'),
                    student_id: localStorage.getItem('student_id'),
                    preceptor_id: localStorage.getItem('preceptor_id'),
                    admin_id: localStorage.getItem('admin_id')
                });
            }
            const result = await loginAsUser(userId, { returnPath, targetRole });
            if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('Impersonation result:', result);

            if (result.success) {
                switch(result.role) {
                    case "student":
                        if (import.meta.env.VITE_DEBUG_MODE === 'true') { console.log('Navigating to /StudentDashboard'); console.groupEnd(); }
                        navigate("/StudentDashboard");
                        break;
                    case "preceptor":
                        if (import.meta.env.VITE_DEBUG_MODE === 'true') { console.log('Navigating to preceptor path:', result.isAdmin ? '/AdminDashboard' : '/PreceptorDashboard'); console.groupEnd(); }
                        navigate(result.isAdmin ? "/AdminDashboard" : "/PreceptorDashboard");
                        break;
                    case "admin":
                        if (import.meta.env.VITE_DEBUG_MODE === 'true') { console.log('Navigating to /StandaloneAdminDashboard'); console.groupEnd(); }
                        navigate("/StandaloneAdminDashboard");
                        break;
                    default:
                        if (import.meta.env.VITE_DEBUG_MODE === 'true') console.groupEnd();
                        throw new Error("Invalid user type");
                }
            }
        } catch (err) {
            console.error('🔴 [IMPERSONATION][ManageUsers] handleLoginAs failed', {
                message: err?.message,
                stack: err?.stack
            });
            setError(getActionErrorMessage(err, 'load users'));
        }
    };

    const resolveUserId = (user) => {
        if (!user || typeof user !== 'object') return '';
        return (
            user._id ||
            user.id ||
            user.user_id ||
            user.student_id ||
            user.preceptor_id ||
            user.admin_id ||
            ''
        );
    };

    const handleDeleteUser = async () => {
        try {
            setIsLoading(true);
            const response = await axiosInstance.delete(`/crud/delete_user/${userToDelete.id}`);
            
            if (response.status === 200) {
                setError('');
                // Refresh the users list
                await refreshData();
            }
        } catch (err) {
            setError(getActionErrorMessage(err, "delete user"));
        } finally {
            setIsLoading(false);
            setShowDeleteModal(false);
            setUserToDelete(null);
        }
    };

    const handleExportSuccess = (_result) => {};

    const handleExportError = (result) => {
        console.error('Export failed:', result);
        // You can add error notifications here
    };

    // Prepare export data configuration
    const getExportDataConfig = () => {
        try {
            const allUsers = [];
            let exportTitle = 'User Management';
            let exportUserType = '';

            if (selectedTypes.includes('student')) {
                const filteredStudents = filterStudents(students);
                allUsers.push(...filteredStudents.map(user => ({
                    ...user,
                    userType: 'Student',
                    rank: user.rank || 'N/A'
                })));
                exportUserType = 'student';
                exportTitle += ' - Students';
            }
            if (selectedTypes.includes('preceptor')) {
                const filteredPreceptors = filterPreceptors(preceptors);
                allUsers.push(...filteredPreceptors.map(user => ({
                    ...user,
                    userType: 'Preceptor',
                    rank: user.rank || 'N/A'
                })));
                exportUserType = 'preceptor';
                exportTitle += selectedTypes.includes('student') ? ' & Preceptors' : ' - Preceptors';
            }
            if (selectedTypes.includes('admin')) {
                const filteredPreceptorAdmins = filterAdmins(preceptorAdmins, 'preceptor');
                const filteredStandaloneAdmins = filterAdmins(standaloneAdmins, 'standalone');
                
                allUsers.push(...filteredPreceptorAdmins.map(user => ({
                    ...user,
                    userType: 'Preceptor Administrator',
                    adminType: 'Preceptor Admin'
                })));
                
                allUsers.push(...filteredStandaloneAdmins.map(user => ({
                    ...user,
                    userType: 'Standalone Administrator', 
                    adminType: 'Standalone Admin'
                })));
                
                exportUserType = 'admin';
                exportTitle += (selectedTypes.includes('student') || selectedTypes.includes('preceptor')) 
                    ? ' & Administrators' : ' - Administrators';
            }

            if (allUsers.length === 0) {
                return null;
            }

            // Create columns for export
            const columns = [
                { key: 'userType', label: 'User Type' },
                { key: 'firstname', label: 'First Name' },
                { key: 'lastname', label: 'Last Name' },
                { key: 'email', label: 'Email' },
                { key: 'phone', label: 'Phone' },
                { key: 'rank', label: 'Rank' },
                { key: 'speciality', label: 'Speciality' },
                { key: 'institution', label: 'Institution' },
                { key: 'adminType', label: 'Admin Type' }
            ];
            
            const filename = `user_management_${selectedTypes.join('_')}_${new Date().toISOString().split('T')[0]}`;
            
            return prepareExportData(allUsers, columns, exportTitle, filename);
        } catch (error) {
            console.error('Export failed:', error);
            return null;
        }
    };

    const filterUsers = (users) => {
        if (!users) return [];
        
        return users.filter(user => {
            const searchTerm = searchQuery.toLowerCase().trim();
            return searchTerm === '' || 
                `${user.firstname} ${user.lastname}`.toLowerCase().includes(searchTerm) ||
                (user.email && user.email.toLowerCase().includes(searchTerm)) ||
                (user.speciality && user.speciality.toLowerCase().includes(searchTerm)) ||
                (user.institution && user.institution.toLowerCase().includes(searchTerm)) ||
                (user._id && user._id.toLowerCase().includes(searchTerm));  // Add ID search
        });
    };

    const filterStudents = (students) => {
        return filterUsers(students).filter(student => 
            !studentRank || student.rank === studentRank
        );
    };

    const filterPreceptors = (preceptors) => {
        if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('Filtering preceptors:', {
            preceptors: preceptors.length,
            selectedSpecialities,
            selectedInstitutions,
            preceptorRank
        });
        
        return filterUsers(preceptors).filter(preceptor => {
            // Check if preceptor matches selected specialities (rotations/experiences).
            // A preceptor can hold several (specialityIds), not just their primary
            // (specialityId) one, so both must be checked or multi-speciality
            // preceptors get incorrectly filtered out.
            const specialityMatch = selectedSpecialities.length === 0 ||
                selectedSpecialities.some(specId =>
                    preceptor.specialityId === specId ||
                    preceptor.speciality?._id === specId ||
                    preceptor.speciality === specId ||
                    (Array.isArray(preceptor.specialityIds) && preceptor.specialityIds.includes(specId))
                );

            // Check if preceptor matches selected institutions (same multi-value caveat).
            const institutionMatch = selectedInstitutions.length === 0 ||
                selectedInstitutions.some(instId =>
                    preceptor.institutionId === instId ||
                    preceptor.institution?._id === instId ||
                    preceptor.institution === instId ||
                    (Array.isArray(preceptor.institutionIds) && preceptor.institutionIds.includes(instId))
                );
            
            // Check if preceptor matches the selected rank (preceptorRank holds a rank id)
            const rankMatch = !preceptorRank ||
                getPreceptorRankIds(preceptor, ranks).includes(preceptorRank);
            
            const result = specialityMatch && institutionMatch && rankMatch;
            
            if (!result && import.meta.env.VITE_DEBUG_MODE === 'true') {
                console.log('Preceptor filtered out:', {
                    preceptor: `${preceptor.firstname} ${preceptor.lastname}`,
                    specialityMatch,
                    institutionMatch,
                    rankMatch,
                    preceptorData: {
                        specialityId: preceptor.specialityId,
                        speciality: preceptor.speciality,
                        institutionId: preceptor.institutionId,
                        institution: preceptor.institution,
                        rank: preceptor.rank
                    }
                });
            }
            
            return result;
        });
    };

    const filterAdmins = (admins, type) => {
        if (adminType !== 'all' && adminType !== type) {
            return [];
        }
        return filterUsers(admins);
    };

    const renderFilterPopup = (type) => {
        if (activeFilterPopup !== type) return null;

        const content = (() => {
            switch (type) {
                case 'student':
                    return (
                        <>
                            <div className="multi-select-container">
                                <label className="filter-label">Student Rank</label>
                                <select
                                    value={studentRank}
                                    onChange={(e) => setStudentRank(e.target.value)}
                                    className="filter-select"
                                >
                                    <option value="">All Ranks</option>
                                    {ranks.map((rank) => (
                                        <option key={rank._id} value={rank.name}>{rank.name}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Clear Filters Button */}
                            {studentRank && (
                                <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                                    <button
                                        onClick={() => setStudentRank('')}
                                        style={{
                                            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '8px',
                                            padding: '0.5rem 1rem',
                                            fontSize: '0.8rem',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'translateY(-1px)';
                                            e.currentTarget.style.boxShadow = '0 4px 8px rgba(239, 68, 68, 0.3)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.boxShadow = 'none';
                                        }}
                                    >
                                        🗑️ Clear Filter
                                    </button>
                                </div>
                            )}
                        </>
                    );

                case 'preceptor':
                    return (
                        <>
                            <div className="multi-select-container">
                                <label className="filter-label">Rotations/Experiences</label>
                                <SearchableDropdown
                                    options={specialities}
                                    selectedValues={selectedSpecialities}
                                    onSelectionChange={setSelectedSpecialities}
                                    placeholder="Select rotations/experiences..."
                                    optionLabel="speciality"
                                    optionValue="_id"
                                    multiple={true}
                                    size="medium"
                                    disableClickOutside={true}
                                />
                            </div>

                            <div className="multi-select-container">
                                <label className="filter-label">Institutions</label>
                                <SearchableDropdown
                                    options={institutions}
                                    selectedValues={selectedInstitutions}
                                    onSelectionChange={setSelectedInstitutions}
                                    placeholder="Select institutions..."
                                    optionLabel="name"
                                    optionValue="_id"
                                    multiple={true}
                                    size="medium"
                                    disableClickOutside={true}
                                />
                            </div>

                            <div className="multi-select-container">
                                <label className="filter-label">Rank</label>
                                <select
                                    value={preceptorRank}
                                    onChange={(e) => setPreceptorRank(e.target.value)}
                                    className="filter-select"
                                >
                                    <option value="">All Ranks</option>
                                    {ranks.map((rank) => (
                                        <option key={rank._id} value={rank._id}>{rank.name}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Clear Filters Button */}
                            {(selectedSpecialities.length > 0 || selectedInstitutions.length > 0 || preceptorRank) && (
                                <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                                    <button
                                        onClick={() => {
                                            setSelectedSpecialities([]);
                                            setSelectedInstitutions([]);
                                            setPreceptorRank('');
                                        }}
                                        style={{
                                            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '8px',
                                            padding: '0.5rem 1rem',
                                            fontSize: '0.8rem',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'translateY(-1px)';
                                            e.currentTarget.style.boxShadow = '0 4px 8px rgba(239, 68, 68, 0.3)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.boxShadow = 'none';
                                        }}
                                    >
                                        🗑️ Clear All Filters
                                    </button>
                                </div>
                            )}
                        </>
                    );

                case 'admin':
                    return (
                        <>
                            <div className="multi-select-container">
                                <label className="filter-label">Admin Type</label>
                                <select
                                    value={adminType}
                                    onChange={(e) => setAdminType(e.target.value)}
                                    className="filter-select"
                                >
                                    <option value="all">All Administrators</option>
                                    <option value="preceptor">Preceptor Administrators</option>
                                    <option value="standalone">Standalone Administrators</option>
                                </select>
                            </div>

                            {/* Clear Filters Button */}
                            {adminType !== 'all' && (
                                <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                                    <button
                                        onClick={() => setAdminType('all')}
                                        style={{
                                            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '8px',
                                            padding: '0.5rem 1rem',
                                            fontSize: '0.8rem',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'translateY(-1px)';
                                            e.currentTarget.style.boxShadow = '0 4px 8px rgba(239, 68, 68, 0.3)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.boxShadow = 'none';
                                        }}
                                    >
                                        🗑️ Clear Filter
                                    </button>
                                </div>
                            )}
                        </>
                    );

                default:
                    return null;
            }
        })();

        return (
            <div className="filter-popup" onClick={(e) => e.stopPropagation()}>
                {content}
            </div>
        );
    };

    const DROPDOWN_MENU_WIDTH = 200;

    const ActionDropdown = ({ user, userType }) => {
        const targetUserId = resolveUserId(user);
        const [isOpen, setIsOpen] = useState(false);
        const wrapperRef = useRef(null);
        const buttonRef = useRef(null);

        useEffect(() => {
            if (!isOpen) return;
            const handleClickOutside = (e) => {
                if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                    setIsOpen(false);
                }
            };
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }, [isOpen]);

        const handleToggle = () => {
            setIsOpen(!isOpen);
        };

        const dropdownMenu = isOpen && (
            <div
                className="manage-users-action-dropdown-menu"
                style={{
                    position: 'absolute',
                    top: 'calc(100% + 0.5rem)',
                    right: 0,
                    width: DROPDOWN_MENU_WIDTH,
                    background: 'white',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
                    border: '1px solid #e2e8f0',
                    zIndex: 10001,
                }}
            >
                        <button 
                            onClick={() => {
                                if (import.meta.env.VITE_DEBUG_MODE === 'true') {
                                    console.group('🧭 [IMPERSONATION][ManageUsers] Row action context');
                                    console.log('User type section:', userType);
                                    console.log('Resolved targetUserId:', targetUserId);
                                    console.log('Raw user id candidates:', {
                                        _id: user?._id,
                                        id: user?.id,
                                        user_id: user?.user_id,
                                        student_id: user?.student_id,
                                        preceptor_id: user?.preceptor_id,
                                        admin_id: user?.admin_id
                                    });
                                    console.log('Row identity:', {
                                        firstname: user?.firstname,
                                        lastname: user?.lastname,
                                        email: user?.email,
                                        rank: user?.rank
                                    });
                                    console.groupEnd();
                                }
                                handleLoginAs(targetUserId, userType);
                                setIsOpen(false);
                            }}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem',
                                padding: '0.875rem 1rem',
                                width: '100%',
                                textAlign: 'left',
                                border: 'none',
                                background: 'transparent',
                                color: '#1e293b',
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)';
                                e.currentTarget.style.color = '#1d4ed8';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent';
                                e.currentTarget.style.color = '#1e293b';
                            }}
                        >
                            <span style={{ fontSize: '1rem' }}>👤</span>
                            Login as User
                        </button>
                        <button 
                            onClick={() => {
                                setModifyUserData({ userId: targetUserId, userType: userType });
                                setShowModifyModal(true);
                                setIsOpen(false);
                            }}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem',
                                padding: '0.875rem 1rem',
                                width: '100%',
                                textAlign: 'left',
                                border: 'none',
                                background: 'transparent',
                                color: '#1e293b',
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)';
                                e.currentTarget.style.color = '#15803d';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent';
                                e.currentTarget.style.color = '#1e293b';
                            }}
                        >
                            <span style={{ fontSize: '1rem' }}>✏️</span>
                            Modify User
                        </button>
                        <button
                            onClick={() => {
                                setDocsModalData({ userId: targetUserId, userName: `${user.firstname} ${user.lastname}` });
                                setShowDocsModal(true);
                                setIsOpen(false);
                            }}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem',
                                padding: '0.875rem 1rem',
                                width: '100%',
                                textAlign: 'left',
                                border: 'none',
                                background: 'transparent',
                                color: '#1e293b',
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(135deg, #fdf4ff 0%, #f3e8ff 100%)';
                                e.currentTarget.style.color = '#7e22ce';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent';
                                e.currentTarget.style.color = '#1e293b';
                            }}
                        >
                            <span style={{ fontSize: '1rem' }}>📄</span>
                            View Documents
                        </button>
                        <button
                            onClick={() => {
                                setUserToDelete({ id: targetUserId, name: `${user.firstname} ${user.lastname}` });
                                setShowDeleteModal(true);
                                setIsOpen(false);
                            }}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem',
                                padding: '0.875rem 1rem',
                                width: '100%',
                                textAlign: 'left',
                                border: 'none',
                                background: 'transparent',
                                color: '#dc2626',
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)';
                                e.currentTarget.style.color = '#b91c1c';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent';
                                e.currentTarget.style.color = '#dc2626';
                            }}
                        >
                            <span style={{ fontSize: '1rem' }}>🗑️</span>
                            Delete User
                        </button>
            </div>
        );

        return (
            <div 
                className={`action-dropdown ${isOpen ? 'open' : ''}`} 
                ref={wrapperRef}
                style={{ position: 'relative', display: 'inline-block' }}
                data-open={isOpen}
            >
                <button 
                    ref={buttonRef}
                    onClick={handleToggle}
                    style={{
                        background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                        color: 'white',
                        border: 'none',
                        padding: '0.5rem 1rem',
                        borderRadius: '8px',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 2px 4px rgba(59, 130, 246, 0.2)',
                        whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(59, 130, 246, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(59, 130, 246, 0.2)';
                    }}
                >
                    Actions 
                    <span style={{ fontSize: '0.75rem', transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
                </button>
                {dropdownMenu}
            </div>
        );
    };

    const renderUserSection = (users, title, userType) => {
        if (!users || users.length === 0) return null;

        const showRank = title.includes('Student') || 
                        title.includes('Preceptor') || 
                        title.includes('Preceptor Administrator');

        // Preceptors may hold several ranks; students carry a single rank name.
        const formatRank = (user) => (
            Array.isArray(user?.rankIds) ? formatPreceptorRanks(user, ranks) : (user?.rank || 'N/A')
        );

        // Get icon based on user type
        const getUserIcon = () => {
            if (title.toLowerCase().includes('student')) return <FiUsers style={{ fontSize: '1.5rem', color: '#3b82f6' }} />;
            if (title.toLowerCase().includes('preceptor')) return <FiUserCheck style={{ fontSize: '1.5rem', color: '#10b981' }} />;
            return <FiUserPlus style={{ fontSize: '1.5rem', color: '#8b5cf6' }} />;
        };

        return (
            <div style={{
                background: 'white',
                borderRadius: '16px',
                marginBottom: '2rem',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(226, 232, 240, 0.8)',
                overflow: 'hidden'
            }}>
                {/* Section Header */}
                <div style={{
                    background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                    padding: '1.5rem 2rem',
                    borderBottom: '1px solid #e2e8f0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem'
                    }}>
                        <div style={{
                            background: 'white',
                            borderRadius: '12px',
                            padding: '0.75rem',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                        }}>
                            {getUserIcon()}
                        </div>
                        <div>
                            <h2 style={{
                                margin: 0,
                                fontSize: '1.5rem',
                                fontWeight: 700,
                                color: '#1e293b'
                            }}>
                                {title}
                            </h2>
                            <p style={{
                                margin: '0.25rem 0 0 0',
                                fontSize: '0.875rem',
                                color: '#6b7280'
                            }}>
                                {users.length} {users.length === 1 ? 'user' : 'users'} found
                            </p>
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div style={{ overflowX: 'auto' }}>
                    <table style={{
                        width: '100%',
                        borderCollapse: 'collapse'
                    }}>
                        <thead>
                            <tr style={{
                                background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 50%, #1e3a8a 100%)'
                            }}>
                                <th style={{
                                    color: 'white',
                                    fontWeight: 700,
                                    padding: '1rem 1.5rem',
                                    textAlign: 'left',
                                    fontSize: '0.875rem',
                                    letterSpacing: '0.05em',
                                    textTransform: 'uppercase'
                                }}>
                                    ID
                                </th>
                                <th style={{
                                    color: 'white',
                                    fontWeight: 700,
                                    padding: '1rem 1.5rem',
                                    textAlign: 'left',
                                    fontSize: '0.875rem',
                                    letterSpacing: '0.05em',
                                    textTransform: 'uppercase'
                                }}>
                                    Name
                                </th>
                                <th style={{
                                    color: 'white',
                                    fontWeight: 700,
                                    padding: '1rem 1.5rem',
                                    textAlign: 'left',
                                    fontSize: '0.875rem',
                                    letterSpacing: '0.05em',
                                    textTransform: 'uppercase'
                                }}>
                                    Email
                                </th>
                                <th style={{
                                    color: 'white',
                                    fontWeight: 700,
                                    padding: '1rem 1.5rem',
                                    textAlign: 'left',
                                    fontSize: '0.875rem',
                                    letterSpacing: '0.05em',
                                    textTransform: 'uppercase'
                                }}>
                                    Phone
                                </th>
                                {showRank && (
                                    <th style={{
                                        color: 'white',
                                        fontWeight: 700,
                                        padding: '1rem 1.5rem',
                                        textAlign: 'left',
                                        fontSize: '0.875rem',
                                        letterSpacing: '0.05em',
                                        textTransform: 'uppercase'
                                    }}>
                                        Rank
                                    </th>
                                )}
                                <th style={{
                                    color: 'white',
                                    fontWeight: 700,
                                    padding: '1rem 1.5rem',
                                    textAlign: 'left',
                                    fontSize: '0.875rem',
                                    letterSpacing: '0.05em',
                                    textTransform: 'uppercase'
                                }}>
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((user, index) => (
                                <tr key={resolveUserId(user) || index} style={{
                                    borderBottom: '1px solid #f1f5f9',
                                    transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = '#f8fafc';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'white';
                                }}
                                >
                                    <td style={{
                                        padding: '1rem 1.5rem',
                                        fontSize: '0.875rem',
                                        color: '#6b7280',
                                        fontFamily: 'monospace'
                                    }}>
                                        {resolveUserId(user) || 'N/A'}
                                    </td>
                                    <td style={{
                                        padding: '1rem 1.5rem',
                                        fontSize: '0.9rem',
                                        color: '#1e293b',
                                        fontWeight: 500
                                    }}>
                                        {user.firstname} {user.lastname}
                                    </td>
                                    <td style={{
                                        padding: '1rem 1.5rem',
                                        fontSize: '0.875rem',
                                        color: '#3b82f6'
                                    }}>
                                        {user.email}
                                    </td>
                                    <td style={{
                                        padding: '1rem 1.5rem',
                                        fontSize: '0.875rem',
                                        color: '#6b7280'
                                    }}>
                                        {user.phone || 'N/A'}
                                    </td>
                                    {showRank && (
                                        <td style={{
                                            padding: '1rem 1.5rem',
                                            fontSize: '0.875rem'
                                        }}>
                                            <span style={{
                                                background: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)',
                                                color: '#0369a1',
                                                padding: '0.25rem 0.75rem',
                                                borderRadius: '20px',
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em'
                                            }}>
                                                {formatRank(user)}
                                            </span>
                                        </td>
                                    )}
                                    <td style={{
                                        padding: '1rem 1.5rem'
                                    }}>
                                        <ActionDropdown 
                                            user={user} 
                                            userType={title.toLowerCase().includes('preceptor admin') ? 'preceptor-admin' : 
                                                    title.toLowerCase().includes('standalone') ? 'standalone-admin' :
                                                    title.toLowerCase().includes('preceptor') ? 'preceptor' : 'student'}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    const handleTypeChange = (e, typeValue) => {
        if (e.target.checked) {
            setSelectedTypes([...selectedTypes, typeValue]);
            // Close any open filter popup when selecting a new type
            setActiveFilterPopup(null);
        } else {
            setSelectedTypes(selectedTypes.filter(t => t !== typeValue));
            // Reset filters and close popup when type is unselected
            setActiveFilterPopup(null);
            switch (typeValue) {
                case 'student':
                    setStudentRank('');
                    break;
                case 'preceptor':
                    setPreceptorRank('');
                    setPreceptorSpeciality('');
                    setPreceptorInstitution('');
                    setSelectedSpecialities([]);
                    setSelectedInstitutions([]);
                    break;
                case 'admin':
                    setAdminType('all');
                    break;
            }
        }
    };

    const renderCheckboxes = () => (
        <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '1.5rem',
            marginBottom: '1.5rem'
        }}>
            {userTypes.map(type => (
                <div 
                    key={type.value}
                    style={{
                        background: selectedTypes.includes(type.value) 
                            ? 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)' 
                            : 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                        border: selectedTypes.includes(type.value) 
                            ? '2px solid #3b82f6' 
                            : '2px solid #e2e8f0',
                        borderRadius: '16px',
                        padding: '1.5rem',
                        cursor: 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        boxShadow: selectedTypes.includes(type.value) 
                            ? '0 8px 25px rgba(59, 130, 246, 0.15)' 
                            : '0 4px 6px rgba(0, 0, 0, 0.05)',
                        transform: selectedTypes.includes(type.value) ? 'translateY(-2px)' : 'translateY(0)'
                    }}
                    onClick={() => {
                        const e = { target: { checked: !selectedTypes.includes(type.value) } };
                        handleTypeChange(e, type.value);
                    }}
                    onMouseEnter={(e) => {
                        if (!selectedTypes.includes(type.value)) {
                            e.currentTarget.style.transform = 'translateY(-4px)';
                            e.currentTarget.style.boxShadow = '0 8px 20px rgba(0, 0, 0, 0.1)';
                            e.currentTarget.style.borderColor = '#94a3b8';
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (!selectedTypes.includes(type.value)) {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.05)';
                            e.currentTarget.style.borderColor = '#e2e8f0';
                        }
                    }}
                >
                    <input
                        type="checkbox"
                        checked={selectedTypes.includes(type.value)}
                        onChange={() => {}} // Handled by div click
                        style={{ display: 'none' }} // Hide the actual checkbox
                    />
                    
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '1rem'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '1rem'
                        }}>
                            <div style={{
                                background: selectedTypes.includes(type.value) 
                                    ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' 
                                    : 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
                                borderRadius: '12px',
                                padding: '0.75rem',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
                            }}>
                                {type.icon}
                            </div>
                            <div>
                                <h3 style={{
                                    margin: 0,
                                    fontSize: '1.125rem',
                                    fontWeight: 700,
                                    color: selectedTypes.includes(type.value) ? '#1e40af' : '#374151'
                                }}>
                                    {type.label}
                                </h3>
                                <p style={{
                                    margin: '0.25rem 0 0 0',
                                    fontSize: '0.875rem',
                                    color: '#6b7280'
                                }}>
                                    {type.value === 'student' && 'Manage student accounts'}
                                    {type.value === 'preceptor' && 'Manage preceptor accounts'}
                                    {type.value === 'admin' && 'Manage administrator accounts'}
                                </p>
                            </div>
                        </div>
                        
                        <div style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            background: selectedTypes.includes(type.value) 
                                ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' 
                                : '#e2e8f0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease'
                        }}>
                            {selectedTypes.includes(type.value) && (
                                <span style={{ color: 'white', fontSize: '0.75rem', fontWeight: 'bold' }}>✓</span>
                            )}
                        </div>
                    </div>
                    
                    {selectedTypes.includes(type.value) && (
                        <button 
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setActiveFilterPopup(activeFilterPopup === type.value ? null : type.value);
                            }}
                            style={{
                                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '10px',
                                padding: '0.625rem 1.25rem',
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                width: '100%',
                                justifyContent: 'center',
                                transition: 'all 0.2s ease',
                                boxShadow: '0 2px 4px rgba(59, 130, 246, 0.2)'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)';
                                e.currentTarget.style.transform = 'translateY(-1px)';
                                e.currentTarget.style.boxShadow = '0 4px 8px rgba(59, 130, 246, 0.3)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)';
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(59, 130, 246, 0.2)';
                            }}
                        >
                            <FiFilter />
                            Advanced Filters
                        </button>
                    )}
                    
                    {activeFilterPopup === type.value && (
                        <div 
                            className="filter-popup"
                            style={{
                                position: 'absolute',
                                top: '100%',
                                left: '0',
                                right: '0',
                                background: 'white',
                                borderRadius: '16px',
                                padding: '1.5rem',
                                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
                                zIndex: 1000,
                                marginTop: '0.5rem',
                                border: '1px solid #e2e8f0',
                                backdropFilter: 'blur(10px)'
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {renderFilterPopup(type.value)}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );

    const filteredStudents = selectedTypes.includes('student') ? filterStudents(students) : [];
    const filteredPreceptors = selectedTypes.includes('preceptor') ? filterPreceptors(preceptors) : [];
    const filteredPreceptorAdmins = selectedTypes.includes('admin') ? filterAdmins(preceptorAdmins, 'preceptor') : [];
    const filteredStandaloneAdmins = selectedTypes.includes('admin') ? filterAdmins(standaloneAdmins, 'standalone') : [];
    const hasResults = [
        filteredStudents,
        filteredPreceptors,
        filteredPreceptorAdmins,
        filteredStandaloneAdmins
    ].some((group) => group.length > 0);

    const modifyModal = showModifyModal ? (
        <ModifyUserModal
            isOpen={showModifyModal}
            onClose={() => setShowModifyModal(false)}
            userId={modifyUserData.userId}
            userType={modifyUserData.userType}
        />
    ) : null;

    const docsModal = (
        <ViewUserDocumentsModal
            isOpen={showDocsModal}
            onClose={() => setShowDocsModal(false)}
            userId={docsModalData.userId}
            userName={docsModalData.userName}
        />
    );

    const deleteModal = showDeleteModal
        ? ReactDOM.createPortal(
            <div
                className="manage-users-delete-modal-overlay"
                onClick={() => {
                    setShowDeleteModal(false);
                    setUserToDelete(null);
                }}
                role="dialog"
                aria-modal="true"
                aria-labelledby="manage-users-delete-title"
            >
                <div
                    className="manage-users-delete-modal-box"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="modal-header">
                        <h2 id="manage-users-delete-title">Confirm Delete</h2>
                        <button
                            type="button"
                            className="modern-close-button"
                            onClick={() => {
                                setShowDeleteModal(false);
                                setUserToDelete(null);
                            }}
                        >
                            &times;
                        </button>
                    </div>
                    <div className="modal-body">
                        <div className="delete-confirmation">
                            <div className="delete-icon">⚠️</div>
                            <p>
                                Are you sure you want to delete{' '}
                                <strong>{userToDelete?.name || 'this user'}</strong>?
                            </p>
                            <p className="warning-text">This action cannot be undone.</p>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button
                            type="button"
                            className="modern-button secondary"
                            onClick={() => {
                                setShowDeleteModal(false);
                                setUserToDelete(null);
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="modern-button delete"
                            onClick={handleDeleteUser}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Deleting...' : 'Delete User'}
                        </button>
                    </div>
                </div>
            </div>,
            document.body
        )
        : null;

    return (
        <>
            <div className="tool-page">
                <div className="tool-shell">
                <div className="tool-hero">
                    <div>
                        <div className="tool-hero__titleRow">
                            <div className="tool-hero__icon">
                                <FiUsers />
                            </div>
                            <h1 className="tool-hero__title">User Management</h1>
                        </div>
                        <p className="tool-hero__subtitle">View, edit, and manage user accounts across all roles.</p>
                    </div>

                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                            {getExportDataConfig() && (
                                <ExportData
                                    {...getExportDataConfig()}
                                    onSuccess={handleExportSuccess}
                                    onError={handleExportError}
                                    disabled={loading || selectedTypes.length === 0}
                                    buttonText="Export Data"
                                    layout="horizontal"
                                />
                            )}
                            
                            {/* Global Clear All Filters Button */}
                            {(studentRank || preceptorRank || preceptorSpeciality || selectedSpecialities.length > 0 || selectedInstitutions.length > 0 || adminType !== 'all') && (
                                <button 
                                    onClick={() => {
                                        setStudentRank('');
                                        setPreceptorRank('');
                                        setPreceptorSpeciality('');
                                        setSelectedSpecialities([]);
                                        setSelectedInstitutions([]);
                                        setAdminType('all');
                                    }}
                                    style={{
                                        background: 'rgba(239, 68, 68, 0.2)',
                                        backdropFilter: 'blur(10px)',
                                        border: '1px solid rgba(239, 68, 68, 0.3)',
                                        borderRadius: '12px',
                                        padding: '0.75rem 1.5rem',
                                        color: 'white',
                                        fontSize: '0.875rem',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        transition: 'all 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.background = 'rgba(239, 68, 68, 0.3)';
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
                                        e.currentTarget.style.transform = 'translateY(0)';
                                    }}
                                >
                                    🗑️ Clear All Filters
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Filter Summary Display */}
                {(studentRank || preceptorRank || preceptorSpeciality || selectedSpecialities.length > 0 || selectedInstitutions.length > 0 || adminType !== 'all') && (
                    <div style={{
                        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                        border: '1px solid #0ea5e9',
                        borderRadius: '12px',
                        padding: '1rem 1.5rem',
                        marginBottom: '1.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: '1rem'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span style={{ fontSize: '1.1rem' }}>🔍</span>
                            <span style={{ fontWeight: 600, color: '#0c4a6e' }}>Active Filters:</span>
                        </div>
                        
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                            {studentRank && (
                                <span style={{
                                    background: '#0ea5e9',
                                    color: 'white',
                                    padding: '0.25rem 0.75rem',
                                    borderRadius: '20px',
                                    fontSize: '0.8rem',
                                    fontWeight: 600
                                }}>
                                    Student Rank: {studentRank.toUpperCase()}
                                </span>
                            )}
                            
                            {preceptorRank && (
                                <span style={{
                                    background: '#0ea5e9',
                                    color: 'white',
                                    padding: '0.25rem 0.75rem',
                                    borderRadius: '20px',
                                    fontSize: '0.8rem',
                                    fontWeight: 600
                                }}>
                                    Preceptor Rank: {findRank(ranks, preceptorRank)?.name || preceptorRank}
                                </span>
                            )}
                            
                            {selectedSpecialities.length > 0 && (
                                <span style={{
                                    background: '#0ea5e9',
                                    color: 'white',
                                    padding: '0.25rem 0.75rem',
                                    borderRadius: '20px',
                                    fontSize: '0.8rem',
                                    fontWeight: 600
                                }}>
                                    Rotations/Experiences: {selectedSpecialities.length} selected
                                </span>
                            )}
                            
                            {selectedInstitutions.length > 0 && (
                                <span style={{
                                    background: '#0ea5e9',
                                    color: 'white',
                                    padding: '0.25rem 0.75rem',
                                    borderRadius: '20px',
                                    fontSize: '0.8rem',
                                    fontWeight: 600
}}>
                                    Institutions: {selectedInstitutions.length} selected
                                </span>
                            )}

                            {adminType !== 'all' && (
                                <span style={{
                                    background: '#0ea5e9',
                                    color: 'white',
                                    padding: '0.25rem 0.75rem',
                                    borderRadius: '20px',
                                    fontSize: '0.8rem',
                                    fontWeight: 600
                                }}>
                                    Admin Type: {adminType === 'preceptor' ? 'Preceptor Admins' : 'Standalone Admins'}
                                </span>
                            )}
                        </div>
                    </div>
                )}

                <div className="tool-card" style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
                    <div className="modern-short-search">
                        <FiSearch className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search by name, email, speciality, institution, or ID..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <div className="modern-filter-container">
                        {renderCheckboxes()}
                    </div>
                </div>

                <div className="tool-card" style={{ padding: '1.5rem' }}>
                    {error && <div className="modern-error-message">{error}</div>}

                    {loading ? (
                        <div style={{ padding: '2rem 0' }}>
                            <LoadingSpinner text="Loading users..." variant="inline" />
                        </div>
                    ) : selectedTypes.length === 0 ? (
                        <div className="modern-select-prompt">
                            <p>Select at least one user type to view accounts.</p>
                        </div>
                    ) : hasResults ? (
                        <>
                            {renderUserSection(filteredStudents, 'Students', 'student')}
                            {renderUserSection(filteredPreceptors, 'Preceptors', 'preceptor')}
                            {renderUserSection(filteredPreceptorAdmins, 'Preceptor Administrators', 'admin')}
                            {renderUserSection(filteredStandaloneAdmins, 'Standalone Administrators', 'admin')}
                        </>
                    ) : (
                        <div className="modern-no-results">
                            <p>No users found matching the current filters.</p>
                        </div>
                    )}
                </div>
            </div>
            {modifyModal}
            {deleteModal}
            {docsModal}
        </>
    );
};

export default ManageUsers;